import { useEffect, useRef } from "react";
import { FileText } from "lucide-react";
import type { PageRef } from "./document.ts";

/**
 * Draws a worker-produced ImageBitmap. A canvas rather than an <img> because the
 * bitmap arrives already decoded — converting it to a blob URL just to feed an
 * <img> would re-encode every thumbnail for no benefit.
 */
export function Thumb({ page }: { page: PageRef }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !page.bitmap) return;
    canvas.width = page.bitmap.width;
    canvas.height = page.bitmap.height;
    const context = canvas.getContext("2d");
    context?.drawImage(page.bitmap, 0, 0);
  }, [page.bitmap]);

  if (!page.bitmap) {
    return (
      <div className="flex aspect-[1/1.414] w-full items-center justify-center rounded-[6px] bg-cream-2">
        <FileText className="size-6 animate-pulse text-muted" />
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className="block h-auto w-full rounded-[6px] bg-white shadow-[0_1px_3px_rgba(51,41,31,0.18)]"
      style={{
        // The user's rotation is applied visually here and baked in on export.
        transform: `rotate(${page.rotation}deg)`,
        transition: "transform 150ms",
      }}
      aria-hidden="true"
    />
  );
}
